import { Fragment } from 'react';
import Image from 'next/image';

import {
  IS_BOLD,
  IS_CODE,
  IS_ITALIC,
  IS_STRIKETHROUGH,
  IS_SUBSCRIPT,
  IS_SUPERSCRIPT,
  IS_UNDERLINE,
} from './nodeFormat';

import { Link } from '@/elements/link/link.component';
import { Caption, Blockquote, PostMediaWrap } from '@/styles/global.elements';

export function serializeLexical({ nodes }) {
  return (
    <Fragment>
      {nodes?.map((node, index) => {
        if (node == null) {
          return null;
        }

        if (node.type === 'text') {
          let text = <Fragment key={index}>{node.text}</Fragment>;
          if (node.format & IS_BOLD) {
            text = (
              <strong key={index} style={{ fontWeight: 'bold' }}>
                {text}
              </strong>
            );
          }
          if (node.format & IS_ITALIC) {
            text = (
              <em key={index} style={{ fontStyle: 'italic' }}>
                {text}
              </em>
            );
          }
          if (node.format & IS_STRIKETHROUGH) {
            text = (
              <span key={index} style={{ textDecoration: 'line-through' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_UNDERLINE) {
            text = (
              <span key={index} style={{ textDecoration: 'underline' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_CODE) {
            text = <code key={index}>{node.text}</code>;
          }
          if (node.format & IS_SUBSCRIPT) {
            text = <sub key={index}>{text}</sub>;
          }
          if (node.format & IS_SUPERSCRIPT) {
            text = <sup key={index}>{text}</sup>;
          }

          return text;
        }

        const serializedChildrenFn = (node) => {
          if (node.children == null) {
            return null;
          }
          if (node.children.length === 0) {
            return <br />;
          } else {
            if (node?.type === 'list' && node?.listType === 'check') {
              for (const item of node.children) {
                if ('checked' in item) {
                  if (!item?.checked) {
                    item.checked = false;
                  }
                }
              }
            }
            return serializeLexical({ nodes: node.children });
          }
        };

        const serializedChildren = 'children' in node ? serializedChildrenFn(node) : '';

        if (node.type === 'block') {
          const block = node.fields;

          const blockType = block?.blockType;

          if (!block || !blockType) {
            return null;
          }

          switch (blockType) {
            default:
              return null;
          }
        } else {
          switch (node.type) {
            case 'linebreak': {
              return <br key={index} />;
            }
            case 'paragraph': {
              return (
                <p key={index} style={{ lineHeight: 1.2, fontWeight: 300, marginBottom: '6px' }}>
                  {serializedChildren}
                </p>
              );
            }
            case 'heading': {
              const Tag = node?.tag;
              return <Tag key={index}>{serializedChildren}</Tag>;
            }
            case 'list': {
              const Tag = node?.tag;

              return <Tag key={index}>{serializedChildren}</Tag>;
            }
            case 'listitem': {
              if (node?.checked != null) {
                return (
                  <li
                    aria-checked={node.checked ? 'true' : 'false'}
                    key={index}
                    role="checkbox"
                    tabIndex={-1}
                    value={node?.value}
                  >
                    {serializedChildren}
                  </li>
                );
              } else {
                return (
                  <li key={index} value={node?.value}>
                    {serializedChildren}
                  </li>
                );
              }
            }
            case 'quote': {
              return <Blockquote key={index}>{serializedChildren}</Blockquote>;
            }
            case 'upload':
              const mimeType = node.value.mimeType;

              if (mimeType.includes('video')) {
                return (
                  <PostMediaWrap key={index}>
                    <video
                      width={1024}
                      height={512}
                      controls
                      preload="none"
                      style={{ objectFit: 'cover', maxWidth: '100%', height: 'auto' }}
                    >
                      <source src={node.value.url} type={mimeType} />
                    </video>
                    {node.value.description ? <Caption>{node.value.description}</Caption> : null}
                  </PostMediaWrap>
                );
              }

              if (mimeType.includes('image')) {
                return (
                  <PostMediaWrap key={index}>
                    <Image
                      src={node.value.url}
                      width={node.value.width}
                      height={node.value.height}
                      alt={node.value.alt}
                      style={{ objectFit: 'cover', maxWidth: '100%', height: 'auto' }}
                    />
                    {node.value.description ? <Caption>{node.value.description}</Caption> : null}
                  </PostMediaWrap>
                );
              }
            case 'link': {
              const fields = node.fields;

              return (
                <Link
                  key={index}
                  newTab={Boolean(fields?.newTab)}
                  reference={fields.doc}
                  type={fields.linkType === 'internal' ? 'reference' : 'custom'}
                  url={fields.url}
                >
                  {serializedChildren}
                </Link>
              );
            }
            case 'autolink': {
              const fields = node.fields;

              return (
                <Link
                  key={index}
                  newTab={Boolean(fields?.newTab)}
                  reference={fields.doc}
                  type={fields.linkType === 'internal' ? 'reference' : 'custom'}
                  url={fields.url}
                >
                  {serializedChildren}
                </Link>
              );
            }

            default:
              return null;
          }
        }
      })}
    </Fragment>
  );
}
